
<template>
  <div class="home-container">
    <LoadingOverlay :isLoading="isLoading" />
    <auth v-if="showAuth" :mode="authMode" :visible="showAuth" @close="showAuth = false" />
    <ProfileOverlay v-if="showProfile" :visible="showProfile" @close="closeProfile" />
    <About :visible="showAbout" @close="closeAbout" />
    <app-navbar
      v-if="!isAppRoute"
      :showAbout="showAbout"
      :inProfile="showProfile"
      @close-about="closeAbout"
      @open-about="openAbout"
      @open-login="openLogin"
      @open-signup="openSignup"
      @open-profile="openProfile"
      @back-to-live="backToLive"
    ></app-navbar>
    <router-view :showAbout="showAbout" @close-about="closeAbout" @open-signup="openSignup" />
    <app-footer
      @close-about="closeAbout"
      @open-about="openAbout"
      @open-profile="openProfile"
      @open-login="openLogin"
      @open-signup="openSignup"
      :showAbout="showAbout"
    ></app-footer>
  </div>
</template>

<script>
import AppNavbar from "./components/navbar";
import AppFooter from "./components/footer";
import Auth from "./components/auth.vue";
import ProfileOverlay from "./components/profile.vue";
import About from "./components/about.vue";
import LoadingOverlay from "./components/loading-overlay.vue";

export default {
  name: "App",
  components: {
    AppNavbar,
    AppFooter,
    Auth,
    ProfileOverlay,
    About,
    LoadingOverlay
  },
  metaInfo: {
    title: "Nuance Radio",
    meta: [
      {
        property: "og:title",
        content: "Nuance Radio",
      },
    ],
  },
  data() {
    return {
      showAbout: false,
      showAuth: false,
      authMode: 'login',
      showProfile: false,
      isLoading: true,
      dataLoaded: false,
      startTime: Date.now()
    };
  },
  computed: {
    isAppRoute() {
      return this.$route.path === '/app';
    }
  },
  mounted() {
    // Simulate data loading and ensure minimum display time
    this.loadData();
    
    // Force external links to open in system browser when running as home screen app
    this.setupStandaloneLinkHandler();
  },
  methods: {
    async loadData() {
      try {
        // Wait for any initial data loading to complete
        await this.$nextTick();
        
        // Mark data as loaded
        this.dataLoaded = true;
        
        // Check if minimum time has passed, if not, wait
        const elapsed = Date.now() - this.startTime;
        const remainingTime = Math.max(0, 1500 - elapsed);
        
        if (remainingTime > 0) {
          setTimeout(() => {
            this.isLoading = false;
          }, remainingTime);
        } else {
          this.isLoading = false;
        }
      } catch (error) {
        console.error('Error loading data:', error);
        // Still hide loading after minimum time even if there's an error
        setTimeout(() => {
          this.isLoading = false;
        }, 1500);
      }
    },
    openAbout() {
      this.showAbout = true;
    },
    closeAbout() {
      this.showAbout = false;
    },
    openLogin() {
      this.authMode = 'login';
      this.showAuth = true;
    },
    openSignup() {
      this.authMode = 'register';
      this.showAuth = true;
    },
    openProfile() {
      this.showProfile = true;
    },
    closeProfile() {
      this.showProfile = false;
    },
    backToLive() {
      this.showProfile = false;
      this.showAbout = false;
    },
    setupStandaloneLinkHandler() {
      // Detect if running as home screen app (standalone mode)
      // iOS: navigator.standalone is true when added to home screen
      // Android & Modern: matchMedia('(display-mode: standalone)')
      const isIOSStandalone = window.navigator.standalone === true;
      const isStandaloneMedia = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
      const isStandalone = isIOSStandalone || isStandaloneMedia;
      
      if (isStandalone) {
        // Function to process and modify external links
        const processExternalLinks = () => {
          document.querySelectorAll('a[href]').forEach(link => {
            try {
              const url = link.getAttribute('href') || link.href;
              if (url && 
                  typeof url === 'string' &&
                  !url.startsWith('#') && 
                  !url.startsWith('javascript:') && 
                  !url.startsWith('mailto:') &&
                  (url.startsWith('http://') || url.startsWith('https://'))) {
                
                const linkUrl = new URL(url, window.location.origin);
                const currentUrl = new URL(window.location.href);
                
                // If it's an external link, modify it to open in system browser
                if (linkUrl.origin !== currentUrl.origin) {
                  // Remove target="_blank" to prevent in-app opening
                  link.removeAttribute('target');
                  
                  // Add click handler directly to the link
                  link.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    // Navigate to force system browser
                    window.location.href = linkUrl.href;
                    return false;
                  }, true);
                }
              }
            } catch (e) {
              // Ignore errors
            }
          });
        };
        
        // Process links immediately
        processExternalLinks();
        
        // Process links after DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', processExternalLinks);
        }
        
        // Also handle links that might be added dynamically
        const observer = new MutationObserver(processExternalLinks);
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
        
        // Also intercept all clicks as a fallback
        document.addEventListener('click', (event) => {
          let link = event.target.closest('a');
          if (!link && event.target.tagName === 'A') {
            link = event.target;
          }
          
          if (link && link.href) {
            const url = link.href || link.getAttribute('href');
            if (url && 
                typeof url === 'string' &&
                !url.startsWith('#') && 
                !url.startsWith('javascript:') && 
                !url.startsWith('mailto:') &&
                (url.startsWith('http://') || url.startsWith('https://'))) {
              
              try {
                const linkUrl = new URL(url, window.location.origin);
                const currentUrl = new URL(window.location.href);
                
                if (linkUrl.origin !== currentUrl.origin) {
                  event.preventDefault();
                  event.stopPropagation();
                  event.stopImmediatePropagation();
                  window.location.href = linkUrl.href;
                  return false;
                }
              } catch (e) {
                // Ignore errors
              }
            }
          }
        }, true);
      }
    }
  },
};
</script>

<style scoped>
.home-container {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

/* Force mobile layout even in landscape orientation */
@media(max-width: 768px) and (orientation: landscape) {
  .home-container {
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
  }
}
</style>